import { Box, Card, Divider, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../../common/components/Button";
import { toast } from "../../../common/components/Toast";
import { useAuth } from "../../../common/contexts/AuthContext";
import { register } from "../../api/auth";
import { DiscordLoginButton, GoogleLoginButton } from "../../components";
import styles from "./Register.module.css";

export default function Register() {
  const { t } = useTranslation("authentication");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { authenticated } = useAuth();

  // Redirect to home if already authenticated
  useEffect(() => {
    if (authenticated) {
      navigate("/");
    }
  }, [authenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setFieldErrors({});

    const data = new FormData(e.target);
    const user = Object.fromEntries(data.entries());

    try {
      const response = await register(user);
      if (response.success) {
        if (response.verification_required) {
          // Registration successful but email verification required
          toast.success(response.message);
          // Redirect to email verification page with email pre-filled
          navigate(`/verify-email?email=${encodeURIComponent(response.email)}`);
        } else {
          // This case shouldn't happen with current backend logic, but keeping as fallback
          toast.success(t("register.success"));
          navigate("/theme-selection");
        }
      } else {
        // Handle field-specific errors
        if (response.errors && typeof response.errors === "object") {
          setFieldErrors(response.errors);
          setError(response.message || t("register.fixErrors"));
        } else {
          // Handle general errors
          setError(
            response.message || t("register.error") + ". Please try again.",
          );
        }
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
    }

    setLoading(false);
  };

  const getFieldError = (fieldName) => {
    if (fieldErrors[fieldName]) {
      return Array.isArray(fieldErrors[fieldName])
        ? fieldErrors[fieldName][0]
        : fieldErrors[fieldName];
    }
    return "";
  };

  const hasFieldError = (fieldName) => {
    return fieldErrors[fieldName] && fieldErrors[fieldName].length > 0;
  };

  return (
    <div className={styles.registerPageWrapper}>
      {/* Animated background shapes */}
      <div className={styles.backgroundShapes}>
        <div className={`${styles.shape} ${styles.shape1}`}></div>
        <div className={`${styles.shape} ${styles.shape2}`}></div>
        <div className={`${styles.shape} ${styles.shape3}`}></div>
        <div className={`${styles.shape} ${styles.shape4}`}></div>
        <div className={`${styles.shape} ${styles.shape5}`}></div>
      </div>

      <Card
        style={{ maxWidth: 520 }}
        sx={{
          p: 4,
          boxShadow: 1,
          width: "100%",
          zIndex: 10,
          position: "relative",
        }}
      >
        <Typography variant="h2" sx={{ mb: 5, textAlign: "center" }}>
          {t("register.title")}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <GoogleLoginButton disabled={loading} />
        </Box>
        <Box sx={{ mb: 3 }}>
          <DiscordLoginButton disabled={loading} />
        </Box>

        {/* Divider */}
        <Box sx={{ display: "flex", alignItems: "center", my: 3 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="bodySm" sx={{ px: 2, color: "text.secondary" }}>
            {t("register.orDivider")}
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        {error && <span className="text-danger small">{error}</span>}
        <form onSubmit={handleSubmit}>
          <div className="py-2">
            <TextField
              label={t("register.name")}
              name="name"
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("register.namePlaceholder")}
              error={hasFieldError("name")}
              helperText={getFieldError("name")}
            />
          </div>
          <div className="py-2">
            <TextField
              label={t("register.email")}
              name="email"
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("register.emailPlaceholder")}
              error={hasFieldError("email")}
              helperText={getFieldError("email")}
            />
          </div>
          <div className="py-2">
            <TextField
              label={t("register.password")}
              name="password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("register.passwordPlaceholder")}
              error={hasFieldError("password")}
              helperText={getFieldError("password")}
            />
          </div>

          <div className="py-2 mt-3">
            <Button variant="primary" size="lg" type="submit" loading={loading}>
              {t("register.submit")}
            </Button>
          </div>
        </form>

        {/* Sign in link */}
        <div className="d-flex justify-content-center align-items-center py-4">
          <Typography variant="bodyMd" className="px-2">
            {t("register.hasAccount")}
          </Typography>
          <Link to="/login">
            <Typography variant="bodyMd" color="primary" fontWeight={500}>
              {t("register.signIn")}
            </Typography>
          </Link>
        </div>
      </Card>
    </div>
  );
}
